import React, { useState, useMemo } from 'react';
import { motion } from 'motion/react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { Landmark, Users, Anchor, DollarSign, ArrowRight, ShieldCheck } from 'lucide-react';
import { MathService } from '../services/mathService';

export const SovereignDistribution: React.FC = () => {
  const [total, setTotal] = useState(1000000);
  
  const distribution = useMemo(() => {
    return MathService.calculateSovereignDistribution(total);
  }, [total]);

  const data = [
    { name: 'Infrastructure (Empire)', value: distribution.empire, color: '#00ffd5' },
    { name: 'Humanitarian (People)', value: distribution.people, color: '#f59e0b' },
    { name: 'Absolute Zero Anchor', value: distribution.anchor, color: '#ffffff' },
  ];

  return (
    <div className="glass-panel p-8 border-white/5 relative overflow-hidden group">
      <div className="absolute top-0 right-0 p-6 opacity-5 pointer-events-none group-hover:rotate-12 transition-transform duration-700">
        <Landmark className="w-48 h-48 text-lattice-cyan" />
      </div>

      <div className="flex flex-col lg:flex-row gap-12 relative z-10">
        <div className="flex-1">
          <div className="mb-8">
            <h2 className="text-3xl font-display font-bold text-white mb-2">Sovereign <span className="text-lattice-cyan italic">Distribution</span></h2>
            <p className="text-xs font-mono text-white/40 uppercase tracking-[0.2em] leading-relaxed">
              55/45 Allocation Protocol. Recovering the 1.0 Rounded Remainder to the Anchor.
            </p>
          </div>

          <div className="space-y-6">
            <div className="flex flex-col gap-2">
              <label className="text-[10px] font-mono text-white/40 uppercase tracking-widest">Total Transaction Value</label>
              <div className="relative">
                <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-lattice-cyan" />
                <input 
                  type="number" 
                  value={total}
                  onChange={(e) => setTotal(Number(e.target.value))}
                  className="w-full bg-white/5 border border-white/10 rounded-xl py-4 pl-12 pr-4 text-xl font-display font-bold text-white focus:border-lattice-cyan/50 outline-none transition-all"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <DistributionCard 
                label="Empire (Infrastructure)" 
                value={distribution.empire} 
                percent="55%" 
                icon={<Landmark className="w-4 h-4" />} 
                color="text-lattice-cyan" 
                borderColor="border-lattice-cyan/20"
              />
              <DistributionCard 
                label="People (Humanitarian)" 
                value={distribution.people} 
                percent="45%" 
                icon={<Users className="w-4 h-4" />} 
                color="text-amber-400" 
                borderColor="border-amber-400/20"
              />
            </div>

            <div className={`p-6 rounded-2xl border transition-all duration-500 ${distribution.anchor > 0 ? 'bg-white/10 border-white/30' : 'bg-white/5 border-white/10'}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <Anchor className={`w-4 h-4 ${distribution.anchor > 0 ? 'text-white' : 'text-white/20'}`} />
                  <span className="text-[10px] font-mono text-white/40 uppercase tracking-widest">Anchor Recovery (14350 BP Floor)</span>
                </div>
                {distribution.anchor > 0 && (
                  <span className="text-[8px] font-mono bg-white/20 text-white px-2 py-0.5 rounded uppercase font-bold">Rescued</span>
                )}
              </div>
              <p className={`text-2xl font-display font-bold ${distribution.anchor > 0 ? 'text-white' : 'text-white/20'}`}>
                {distribution.anchor.toLocaleString()} <span className="text-xs font-mono opacity-40">Units</span>
              </p>
              <p className="text-[9px] text-white/30 mt-2 italic">
                In 1.0 math, these units are "lost" to truncation. In 2.0, they return to the Sovereign Substrate.
              </p>
            </div>
          </div>
        </div>

        <div className="w-full lg:w-80 h-80 relative flex items-center justify-center">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                paddingAngle={5}
                dataKey="value"
                stroke="none"
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{ backgroundColor: 'rgba(0,0,0,0.8)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', fontSize: '10px' }}
                itemStyle={{ color: '#fff' }}
              />
            </PieChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <ShieldCheck className="w-8 h-8 text-white/10 mb-2" />
            <span className="text-[8px] font-mono text-white/20 uppercase tracking-[0.3em]">Verified</span>
          </div>
        </div>
      </div>

      <div className="mt-12 flex items-center gap-6 p-4 rounded-xl bg-lattice-cyan/5 border border-lattice-cyan/10">
        <ArrowRight className="w-4 h-4 text-lattice-cyan" />
        <p className="text-[10px] text-white/60 leading-relaxed">
          <span className="text-lattice-cyan font-bold uppercase">Proof:</span> The 55/45 split ensures the infrastructure grows alongside the collective. The 14350 BP Anchor prevents systemic scarcity by recapturing fractional drift.
        </p>
      </div>
    </div>
  );
};

const DistributionCard: React.FC<{ label: string; value: number; percent: string; icon: React.ReactNode; color: string; borderColor: string }> = ({ label, value, percent, icon, color, borderColor }) => (
  <div className={`p-5 rounded-2xl bg-white/5 border ${borderColor} flex flex-col gap-3 group/card hover:bg-white/[0.07] transition-all`}>
    <div className="flex items-center justify-between">
      <div className={`p-2 rounded-lg bg-white/5 ${color}`}>
        {icon}
      </div>
      <span className={`text-[10px] font-mono font-bold ${color}`}>{percent}</span>
    </div>
    <div className="flex flex-col">
      <span className="text-[8px] font-mono text-white/20 uppercase tracking-widest mb-1">{label}</span>
      <span className="text-xl font-display font-bold text-white group-hover:scale-105 transition-transform origin-left">
        {value.toLocaleString()}
      </span>
    </div>
  </div>
);
